import React, {useEffect, useRef, useState} from 'react';

const easeOutExpo = (t) => (t === 1 ? 1 : 1 - Math.pow(2, -10 * t));

/**
 * Smoothly animates from previous numeric value to new value.
 * On first mount counts up from 0 for a Stripe-style reveal.
 * Props:
 *   value: number to display
 *   duration: animation length in ms (default 700)
 *   format: function(n) -> string, applied to each interpolated frame
 */
export default function AnimatedNumber({value, duration = 700, format}) {
    const [display, setDisplay] = useState(0);
    const prevRef = useRef(0);
    const rafRef = useRef(null);
    const startRef = useRef(0);

    useEffect(() => {
        const from = prevRef.current;
        const to = Number(value) || 0;
        if (from === to) {
            setDisplay(to);
            return;
        }
        startRef.current = performance.now();

        const step = (now) => {
            const elapsed = now - startRef.current;
            const t = Math.min(1, elapsed / duration);
            const eased = easeOutExpo(t);
            const current = from + (to - from) * eased;
            setDisplay(current);
            prevRef.current = current;
            if (t < 1) {
                rafRef.current = requestAnimationFrame(step);
            } else {
                prevRef.current = to;
            }
        };
        rafRef.current = requestAnimationFrame(step);
        return () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
        };
    }, [value, duration]);

    const text = format ? format(display) : display.toFixed(2);
    return <>{text}</>;
}
